import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MdSearch,
  MdChevronLeft,
  MdChevronRight,
  MdFilterList,
  MdSettings,
  MdClose,
} from 'react-icons/md';
import employeeAttendanceHrAPI from '../../api/employeeAttendanceHrAPI';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { parseOdooDateTime } from '../../utils/attendanceFormatters';
import {
  daysAgoIsoLocal,
  fetchWithHrToken,
  HR_SESSION_ERROR,
  todayIsoLocal,
} from '../../utils/hrApiHelpers';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';

const PAGE_SIZE = 80;

function formatPunchTime(dateTimeStr) {
  const d = parseOdooDateTime(dateTimeStr);
  if (!d) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function punchTypeClass(type) {
  const key = String(type || '')
    .toLowerCase()
    .replace(/[\s_]+/g, '-');
  if (key.includes('check-in') || key === 'in' || key === '0') {
    return 'ea-odoo-punch-type--checkin';
  }
  if (key.includes('check-out') || key === 'out' || key === '1') {
    return 'ea-odoo-punch-type--checkout';
  }
  if (key.includes('ignored') || key.includes('ignore')) {
    return 'ea-odoo-punch-type--ignored';
  }
  return 'ea-odoo-punch-type--muted';
}

function formatPunchTypeLabel(type) {
  if (type == null || type === false || type === '') return '';
  const raw = String(type);
  if (/^\d+$/.test(raw)) {
    if (raw === '0') return 'Check-in';
    if (raw === '1') return 'Check-out';
  }
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Odoo attendance line label: "Name - MM/DD/YYYY" */
function formatAttendanceLine(employeeName, punchTime, existing) {
  if (existing && existing !== false) {
    const text = String(existing);
    if (/\d{2}\/\d{2}\/\d{4}/.test(text) || text.includes(' - ')) return text;
  }
  const name = employeeName && employeeName !== false ? String(employeeName).trim() : '';
  const d = parseOdooDateTime(punchTime);
  if (!name || !d) return existing && existing !== false ? String(existing) : '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${name} - ${mm}/${dd}/${yyyy}`;
}

function normalizePunch(p) {
  const punchTime =
    p.punch_time || p.datetime || p.time || p.punch_datetime || p.create_date || '';
  const employeeName =
    p.employee_name ||
    (Array.isArray(p.employee_id) ? p.employee_id[1] : null) ||
    p.employee ||
    '';
  const deviceName =
    p.device_name ||
    (Array.isArray(p.device_id) ? p.device_id[1] : null) ||
    p.device ||
    p.device_no ||
    '';
  const deviceEmployeeNo =
    p.device_employee_no ??
    p.employee_no ??
    p.device_user_id ??
    p.hikvision_id ??
    p.pin ??
    '';
  const punchType = p.punch_type ?? p.type ?? p.attendance_status ?? p.punch_state;
  const applied =
    p.applied_to_attendance === true ||
    p.applied === true ||
    p.is_applied === true ||
    Boolean(p.attendance_id || p.attendance_line_id);
  const existingLine =
    p.attendance_line ||
    p.attendance_name ||
    (Array.isArray(p.attendance_id) ? p.attendance_id[1] : null) ||
    '';

  return {
    id: p.id,
    punch_time: punchTime,
    device_employee_no: deviceEmployeeNo === false ? '' : deviceEmployeeNo,
    employee_name: employeeName === false ? '' : employeeName,
    punch_type: punchType,
    device_name: deviceName === false ? '' : deviceName,
    applied_to_attendance: applied,
    attendance_line: formatAttendanceLine(employeeName, punchTime, existingLine),
    matched: Boolean(employeeName && employeeName !== false),
    raw: p,
  };
}

/**
 * Odoo menu: Attendance → Hikvision Punches
 */
export default function HrHikvisionPunchesPage() {
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();
  const [dateFrom] = useState(daysAgoIsoLocal(30));
  const [dateTo] = useState(todayIsoLocal());
  const [punches, setPunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiMissing, setApiMissing] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [matchedOnly, setMatchedOnly] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0);
      setSearch(searchInput.trim());
    }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setApiMissing(false);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.getHikvisionPunches(
        { date_from: dateFrom, date_to: dateTo, limit: 2000 },
        token
      )
    );
    if (fetchError === 'no_token') {
      setError(HR_SESSION_ERROR);
      setPunches([]);
      setLoading(false);
      return;
    }
    if (fetchError) {
      if (/404|not available|not found/i.test(fetchError)) {
        setApiMissing(true);
        setError(null);
      } else {
        setError(fetchError);
      }
      setPunches([]);
      setLoading(false);
      return;
    }
    const rows = (data.punches || data.records || data.lines || []).map(normalizePunch);
    setPunches(rows);
    setSelected(new Set());
    setLoading(false);
  }, [ensureHrToken, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let list = punches;
    if (matchedOnly) {
      list = list.filter((p) => p.matched);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((p) => {
        const hay = [
          p.employee_name,
          p.device_name,
          p.device_employee_no,
          p.punch_type,
          p.attendance_line,
          p.punch_time,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [punches, matchedOnly, search]);

  const total = filtered.length;
  const pageRows = useMemo(
    () => filtered.slice(offset, offset + PAGE_SIZE),
    [filtered, offset]
  );
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  const allVisibleSelected =
    pageRows.length > 0 && pageRows.every((p) => selected.has(p.id ?? p.punch_time));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        pageRows.forEach((p) => next.delete(p.id ?? p.punch_time));
      } else {
        pageRows.forEach((p) => next.add(p.id ?? p.punch_time));
      }
      return next;
    });
  };

  const toggleOne = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="ea-hr-dash ea-odoo-emp ea-odoo-hik-punches">
      <div className="ea-odoo-control">
        <div className="ea-odoo-control__left">
          <div className="ea-odoo-control__title">Hikvision Punches</div>
          <button
            type="button"
            className="ea-odoo-icon-btn"
            title="Settings"
            aria-label="Settings"
            disabled
          >
            <MdSettings size={18} />
          </button>
        </div>

        <div className="ea-odoo-search ea-odoo-search--chips">
          <MdSearch className="ea-odoo-search__icon" aria-hidden />
          <button
            type="button"
            className="ea-odoo-search__filter"
            title="Filters"
            aria-label="Filters"
            onClick={() => setMatchedOnly((v) => !v)}
          >
            <MdFilterList size={18} />
          </button>
          {matchedOnly && (
            <span className="ea-odoo-search-chip">
              Matched employees
              <button
                type="button"
                className="ea-odoo-search-chip__close"
                aria-label="Remove Matched employees filter"
                onClick={() => {
                  setMatchedOnly(false);
                  setOffset(0);
                }}
              >
                <MdClose size={14} />
              </button>
            </span>
          )}
          <input
            type="search"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search punches"
          />
        </div>

        <div className="ea-odoo-control__right">
          <div className="ea-odoo-pager" aria-label="Pagination">
            <span>{loading ? '…' : `${rangeStart}-${rangeEnd} / ${total}`}</span>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canPrev || loading}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              aria-label="Previous page"
            >
              <MdChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canNext || loading}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              aria-label="Next page"
            >
              <MdChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {apiMissing && (
        <div className="ea-hr-api-banner" role="status">
          <strong>Backend API missing.</strong> Odoo has this form, but the HR JSON API does not
          expose it yet. Ask backend to add:{' '}
          <code>POST /api/employee-attendance/hr/hikvision/punches</code>
        </div>
      )}

      {error && (
        <div className="ea-toast error show ea-dash-error" role="alert">
          {error}
        </div>
      )}

      <div className="ea-odoo-list-wrap">
        <table className="ea-odoo-list ea-odoo-att-list ea-odoo-hik-list">
          <thead>
            <tr>
              <th className="ea-odoo-att-list__check">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAll}
                  aria-label="Select all"
                  disabled={pageRows.length === 0}
                />
              </th>
              <th>Punch Time</th>
              <th>Device Employee No</th>
              <th>Employee</th>
              <th>Punch Type</th>
              <th>Device</th>
              <th className="ea-odoo-hik-list__applied" title="Applied to Attendance">
                Applied to Attendance
              </th>
              <th className="ea-odoo-hik-list__line">Attendance Line</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="ea-hr-table__empty">
                  Loading punches…
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="ea-hr-table__empty">
                  {apiMissing
                    ? 'Waiting for Hikvision Punches API.'
                    : 'No punches found.'}
                </td>
              </tr>
            ) : (
              pageRows.map((p, idx) => {
                const rowKey = p.id ?? `${p.punch_time}-${p.device_employee_no}-${idx}`;
                const typeLabel = formatPunchTypeLabel(p.punch_type);
                return (
                  <tr key={rowKey}>
                    <td className="ea-odoo-att-list__check">
                      <input
                        type="checkbox"
                        checked={selected.has(rowKey) || selected.has(p.id)}
                        onChange={() => toggleOne(p.id ?? rowKey)}
                        aria-label={`Select punch ${formatPunchTime(p.punch_time)}`}
                      />
                    </td>
                    <td>{formatPunchTime(p.punch_time)}</td>
                    <td>{p.device_employee_no || ''}</td>
                    <td className="ea-odoo-list__name">{p.employee_name || ''}</td>
                    <td>
                      {typeLabel ? (
                        <span
                          className={`ea-odoo-punch-type ${punchTypeClass(p.punch_type)}`}
                        >
                          {typeLabel}
                        </span>
                      ) : (
                        ''
                      )}
                    </td>
                    <td>{p.device_name || ''}</td>
                    <td className="ea-odoo-hik-list__applied">
                      <input
                        type="checkbox"
                        className="ea-odoo-bool"
                        checked={Boolean(p.applied_to_attendance)}
                        readOnly
                        tabIndex={-1}
                        aria-label="Applied to attendance"
                      />
                    </td>
                    <td className="ea-odoo-hik-list__line">{p.attendance_line || ''}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
